import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Spin, message, Card } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { judgeTaskExecutionApi } from '../../services/api';
import MachineAuditStatsDisplay from '../../components/MachineAuditStatsDisplay';

/**
 * 机审结果统计页面
 * 展示机审场景评测任务执行历史的结果统计数据
 */
function MachineAuditStats() {
  const { executionId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const taskName = searchParams.get('taskName') || '';
  
  const [loading, setLoading] = useState(true);
  const [statsData, setStatsData] = useState(null);
  const [executionInfo, setExecutionInfo] = useState(null);

  useEffect(() => {
    if (executionId) {
      fetchExecutionDetail();
    }
  }, [executionId]);

  const fetchExecutionDetail = async () => {
    setLoading(true);
    try {
      const response = await judgeTaskExecutionApi.getDetail(executionId);
      setExecutionInfo(response);
      
      // 解析 aleResult 字段
      if (response?.aleResult) {
        try {
          const parsedData = typeof response.aleResult === 'string'
            ? JSON.parse(response.aleResult)
            : response.aleResult;
          setStatsData(parsedData);
        } catch (parseError) {
          console.error('解析结果统计数据失败:', parseError);
          message.error('结果统计数据格式错误');
        }
      }
    } catch (error) {
      console.error('获取执行历史详情失败:', error);
      message.error('获取执行历史详情失败');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    // 由于页面是通过 window.open 在新标签页打开的，navigate(-1) 没有历史记录
    // 尝试关闭当前标签页，如果无法关闭则跳转到任务执行历史页面
    if (window.opener) {
      window.close();
    } else {
      navigate('/judge-task');
    }
  };

  return (
    <div 
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)'
      }}
    >
      {/* 顶部导航栏 */}
      <div
        style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          padding: '16px 32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
          >
            返回
          </Button>
          <div style={{ height: '24px', width: '1px', background: '#e8e8e8' }} />
          <h2
            style={{
              margin: 0,
              fontSize: '20px',
              fontWeight: 600,
              color: '#262626'
            }}
          >
            机审结果统计
          </h2>
          {taskName && (
            <span style={{ color: '#666', fontSize: '14px' }}>
              - {taskName}
            </span>
          )}
        </div>
      </div>

      {/* 主内容区域 */}
      <div style={{ padding: '24px 32px' }}>
        <div 
          style={{
            background: 'rgba(255, 255, 255, 0.98)',
            borderRadius: '16px',
            padding: '32px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            minHeight: 'calc(100vh - 140px)'
          }}
        >
          {loading ? (
            <div 
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '400px'
              }}
            >
              <Spin size="large" tip="加载中..." />
            </div>
          ) : (
            <>
              {/* 执行信息摘要 */}
              {executionInfo && (
                <Card 
                  className="mb-6"
                  style={{ 
                    background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)',
                    borderRadius: '12px',
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                  }}
                >
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div style={{
                      background: 'linear-gradient(135deg, #e6f7ff 0%, #f0f5ff 100%)',
                      padding: '16px',
                      borderRadius: '10px'
                    }}>
                      <div className="text-gray-500 text-sm">执行ID</div>
                      <div className="font-semibold text-gray-800">{executionInfo.id}</div>
                    </div>
                    <div style={{
                      background: 'linear-gradient(135deg, #f6ffed 0%, #e6fffb 100%)',
                      padding: '16px',
                      borderRadius: '10px'
                    }}>
                      <div className="text-gray-500 text-sm">任务名称</div>
                      <div className="font-semibold text-gray-800">{executionInfo.taskName || taskName || '-'}</div>
                    </div>
                    <div style={{
                      background: 'linear-gradient(135deg, #fff7e6 0%, #fff1f0 100%)',
                      padding: '16px',
                      borderRadius: '10px'
                    }}>
                      <div className="text-gray-500 text-sm">执行状态</div>
                      <div className="font-semibold" style={{
                        color: executionInfo.status === 'SUCCESS' ? '#52c41a' : 
                               executionInfo.status === 'FAILED' ? '#ff4d4f' : '#1890ff'
                      }}>
                        {executionInfo.status === 'SUCCESS' ? '执行成功' : 
                         executionInfo.status === 'FAILED' ? '执行失败' : 
                         executionInfo.status === 'RUNNING' ? '执行中' : executionInfo.status}
                      </div>
                    </div>
                    <div style={{
                      background: 'linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)',
                      padding: '16px',
                      borderRadius: '10px'
                    }}>
                      <div className="text-gray-500 text-sm">用例总数</div>
                      <div className="font-semibold text-gray-800">{executionInfo.totalCaseCount || 0}</div>
                    </div>
                  </div>
                </Card>
              )}

              {/* 机审统计数据展示 */}
              <MachineAuditStatsDisplay data={statsData} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default MachineAuditStats;
